import { format } from "date-fns";
import { FC } from "react";
import { BillingTransferRequestStatus } from "@common/constants/domains/billing/billingTransferRequest";
import { fullDateFormatWithTime } from "@common/helpers/date";
import type { ClientOrgBillingTransferRequest } from "@common/schemas/client.schema";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";

interface Props {
    request: ClientOrgBillingTransferRequest;
}

const DeclinedTooltipContent: FC<Props> = ({ request }) => {
    const rows = [
        {
            label: "Requested",
            value: format(request.createdAt, fullDateFormatWithTime),
        },
        request.resolvedAt && {
            label: "Declined",
            value: format(request.resolvedAt, fullDateFormatWithTime),
        },
        request.resolvedByName && {
            label: "Declined by",
            value: request.resolvedByName,
        },
        {
            label: "Reason",
            value: request.declineReason ?? "No reason given",
        },
    ].filter((row) => !!row);

    return (
        <VStack gap={2}>
            {rows.map(({ label, value }) => (
                <div key={label}>{`${label}: ${value}`}</div>
            ))}
        </VStack>
    );
};

export const OrgBillingTransferBadge: FC<Props> = ({ request }) => {
    if (request.status === BillingTransferRequestStatus.PENDING) {
        return (
            <Tooltip
                content={`Waiting for ${request.recipientName ?? "recipient"}`}
            >
                <span>
                    <Tag size="sm" color="neutral" outline>
                        Transfer in progress
                    </Tag>
                </span>
            </Tooltip>
        );
    }

    return (
        <Tooltip content={<DeclinedTooltipContent request={request} />}>
            <span>
                <Tag size="sm" color="neutral" outline>
                    Transfer declined
                </Tag>
            </span>
        </Tooltip>
    );
};
